import React, { ReactElement, useContext, useEffect, useState } from "react";
import { ResponsiveLine, Serie } from "@nivo/line";
import { GameInsightAlignmentTrendResponse, PeriodComparisonResponse } from "../api/Requests";
import { UserContext } from "../contexts/UserContext";
import { HttpClient } from "../api/HttpClient";
import { useTheme, Typography, Box, Button, Paper, Grid, Card, CardContent, Skeleton, Alert, ToggleButton, ToggleButtonGroup } from "@mui/material";
import AlignmentBadgeComponent from "../components/AlignmentBadge";
import { alignmentScoreToText, getAlignmentScoreColor, ALIGNMENT_SCORE_THRESHOLDS } from "../utils/alignmentScoreUtils";
import { TrendIcon, TrendDirection } from "../components/TrendIcon";

interface AlignmentTrendProps {
    roomUrlId: string;
}

export default function AlignmentTrend({ roomUrlId }: AlignmentTrendProps): ReactElement {
    const { authToken } = useContext(UserContext);
    const theme = useTheme();
    //textColor that matches the theme when it shifts between dark and light
    const textColor = theme.palette.text.primary;
    const [data, setData] = useState<Serie[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<'30d' | '90d' | '6m' | '1y'>('30d');
    const [periodComparison, setPeriodComparison] = useState<PeriodComparisonResponse | null>(null);
    const [currentScore, setCurrentScore] = useState<number | null>(null);

    const initData: Serie[] = [
        {
            id: "alignment",
            data: [
                { x: "2025-01-10T00:00:00.000Z", y: 50 },
                { x: "2025-01-11T00:00:00.000Z", y: 50 },
                { x: "2025-01-12T00:00:00.000Z", y: 50 },
                { x: "2025-01-13T00:00:00.000Z", y: 50 },
                { x: "2025-01-14T00:00:00.000Z", y: 50 },
            ],
        },
    ];



    const filterDataByDateRange = (dataPoints: readonly { x: string; y: number }[], range: string) => {
        const now = new Date();
        const cutoffDate = new Date();
        
        switch (range) {
            case '30d':
                cutoffDate.setDate(now.getDate() - 30);
                break;
            case '90d':
                cutoffDate.setDate(now.getDate() - 90);
                break;
            case '6m':
                cutoffDate.setMonth(now.getMonth() - 6);
                break;
            case '1y':
                cutoffDate.setFullYear(now.getFullYear() - 1);
                break;
        }
        
        return dataPoints.filter(point => new Date(point.x) >= cutoffDate);
    };

    const getFilteredData = () => {
        if (!data[0]?.data) return data;
        
        const dataPoints = data[0].data.map(point => ({
            x: point.x as string,
            y: point.y as number
        }));
        const filteredPoints = filterDataByDateRange(dataPoints, dateRange);
        return [{
            ...data[0],
            data: filteredPoints
        }];
    };

    // Define Y-axis values for horizontal lines - matching backend alignment score thresholds
    const yAxisIntervalValues = ALIGNMENT_SCORE_THRESHOLDS;

    const CustomLayerWithScoreIntervalExplanation = ({ yScale }: { yScale: (value: number) => number }) => {
        return (
            <>
                {yAxisIntervalValues.map((value, index) => {
                    const nextValue = yAxisIntervalValues[index + 1];
                    if (nextValue !== undefined) {
                        // Skip drawing line at the last threshold before 100 since we'll draw Perfect line at 100 instead
                        if (nextValue === 100) {
                            return null;
                        }
                        
                        const midPoint = (yScale(value) + yScale(nextValue)) / 2;

                        return (
                            <g key={value}>
                                <line
                                    x1={0}
                                    x2="100%"
                                    y1={yScale(value)}
                                    y2={yScale(value)}
                                    stroke={getAlignmentScoreColor(value)}
                                    strokeWidth={1}
                                    strokeDasharray="4 4"
                                />
                                <text
                                    x="92%"
                                    y={midPoint}
                                    textAnchor="end"
                                    fill={getAlignmentScoreColor(nextValue)}
                                    fontSize="12px"
                                    dy="0.35em"
                                >
                                    {alignmentScoreToText(nextValue)}
                                </text>
                            </g>
                        );
                    }
                    return null;
                })}
                {/* Special handling for Perfect line at exactly 100 */}
                <g key="perfect">
                    <line
                        x1={0}
                        x2="100%"
                        y1={yScale(100)}
                        y2={yScale(100)}
                        stroke={getAlignmentScoreColor(100)}
                        strokeWidth={1}
                        strokeDasharray="4 4"
                    />
                    <text
                        x="92%"
                        y={yScale(100) - 8}
                        textAnchor="end"
                        fill={getAlignmentScoreColor(100)}
                        fontSize="12px"
                        dy="0"
                    >
                        Perfect
                    </text>
                </g>
            </>
        );
    };

    useEffect(() => {
        setData(initData);
        setIsLoading(true);
        setError(null);

        const fetchData = async () => {
            try {
                const httpClient = new HttpClient(authToken);
                
                // Map date range to days
                const getLimitDays = (range: string): number | null => {
                    switch (range) {
                        case '30d': return 30;
                        case '90d': return 90;
                        case '6m': return 180;
                        case '1y': return 365;
                        default: return null;
                    }
                };
                
                const limitDays = getLimitDays(dateRange);
                const url = limitDays ? `/rooms/${roomUrlId}/alignment-trend?limitDays=${limitDays}` : `/rooms/${roomUrlId}/alignment-trend`;
                
                // Fetch both alignment trend data and period comparison
                const [trendResponse, comparisonResponse] = await Promise.all([
                    httpClient.get(url) as Promise<GameInsightAlignmentTrendResponse>,
                    httpClient.get(`/rooms/${roomUrlId}/alignment-trend/period-comparison?periodDays=${limitDays || 30}`) as Promise<PeriodComparisonResponse>
                ]);

                // Always set period comparison data
                setPeriodComparison(comparisonResponse);
                
                if (trendResponse.aglinmentPoints && trendResponse.aglinmentPoints.length > 0) {
                    const dataPoints = trendResponse.aglinmentPoints.map((point) => ({
                        x: point.x,
                        y: point.y,
                    }));
                    
                    const formattedData: Serie[] = [
                        {
                            id: "alignment",
                            data: dataPoints,
                        },
                    ];
                    
                    setData(formattedData);
                    setHasData(true);
                    
                    // Set current score from the latest data point
                    const latestPoint = dataPoints[dataPoints.length - 1];
                    setCurrentScore(latestPoint.y);
                } else {
                    setHasData(false);
                    setCurrentScore(null);
                }
            } catch (error) {
                console.error("Failed to fetch alignment trend data:", error);
                setHasData(false);
                setError("Failed to load alignment trends. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };
        
        if (authToken) {
            fetchData();
        } else {
            setIsLoading(false);
        }
    }, [authToken, roomUrlId, dateRange]);

    const renderContent = () => {
        if (error) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="400px" gap={2}>
                    <Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>
                        {error}
                    </Alert>
                    <Button 
                        variant="outlined" 
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box>
                    <Skeleton variant="rectangular" height={400} />
                    <Box mt={2}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                    </Box>
                </Box>
            );
        }

        if (!hasData) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="400px" gap={2}>
                    <Typography variant="h6" color="text.secondary">
                        No alignment data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                        Start playing to see your team&apos;s alignment trends over time.
                    </Typography>
                </Box>
            );
        }

        const filteredData = getFilteredData();
        const dataPoints = filteredData[0]?.data || [];
        
        // Calculate tick values based on actual data points
        const getTickValues = () => {
            if (dataPoints.length === 0) return [];
            
            const dates = dataPoints.map(point => new Date(point.x as string));
            
            if (dates.length <= 10) {
                return dates;
            }
            
            const step = Math.ceil(dates.length / 10);
            const sampledDates = [];
            for (let i = 0; i < dates.length; i += step) {
                sampledDates.push(dates[i]);
            }
            
            if (sampledDates[sampledDates.length - 1] !== dates[dates.length - 1]) {
                sampledDates.push(dates[dates.length - 1]);
            }
            
            return sampledDates;
        };

        return (
            <ResponsiveLine
                data={filteredData.filter((d) => d.id === "alignment")}
                margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
                xScale={{ type: "time", format: "%Y-%m-%dT%H:%M:%S.%LZ", precision: "day" }}
                xFormat="time:%Y-%m-%d"
                yScale={{ type: "linear", min: 0, max: 100, stacked: true, reverse: false }}
                axisLeft={{
                    legend: "Alignment Score",
                    legendOffset: -40,
                    legendPosition: "middle",
                }}
                axisBottom={{
                    format: "%b %d",
                    tickValues: getTickValues(),
                    legend: "Session Date",
                    legendOffset: 40,
                    legendPosition: "middle",
                    tickRotation: -45,
                }}
                curve="monotoneX"
                pointSize={8}
                pointBorderWidth={2}
                pointLabelYOffset={-12}
                pointColor={{ from: "color", modifiers: [] }} 
                colors={{ scheme: "dark2" }}
                lineWidth={3} 
                useMesh={true}
                motionConfig="stiff"
                gridXValues={[]}
                gridYValues={[]}
                tooltip={({ point }) => (
                    <div style={{
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {new Date(point.data.x).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                            })}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: point.color,
                                borderRadius: '50%'
                            }} />
                            <div>
                                <div style={{ fontWeight: '600' }}>
                                    Score: {point.data.y as number}
                                </div>
                                <div style={{ 
                                    fontSize: '12px', 
                                    opacity: 0.8,
                                    color: getAlignmentScoreColor(point.data.y as number)
                                }}>
                                    {alignmentScoreToText(point.data.y as number)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                layers={[
                    "grid",
                    "axes",
                    "areas",
                    "lines",
                    "points",
                    "slices",
                    "mesh",
                    "legends",
                    CustomLayerWithScoreIntervalExplanation,
                ]}
                theme={{
                    axis: {
                        ticks: {
                            text: {
                                fill: textColor,
                                fontSize: 12,
                            },
                        },
                        legend: {
                            text: {
                                fill: textColor,
                            },
                        },
                    },
                    legends: {
                        text: {
                            fill: textColor,
                        },
                    },
                }}
            />
        );
    };

    return (
        <Box>
            {isLoading ? (
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                    {[1, 2, 3, 4].map((index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                            <Card>
                                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Skeleton variant="text" width="60%" height={24} />
                                    <Skeleton variant="text" width="40%" height={48} />
                                    <Skeleton variant="rectangular" width="80px" height={24} sx={{ mt: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (periodComparison || currentScore !== null) && !error ? (
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Current Score
                                </Typography>
                                <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, flex: 1 }}>
                                    {currentScore ? Math.round(currentScore) : 'N/A'}
                                </Typography>
                                <AlignmentBadgeComponent 
                                    label={currentScore ? alignmentScoreToText(currentScore) : 'No Data'}
                                    sx={{ mt: 'auto' }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Trend
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ flex: 1 }}>
                                    {periodComparison && (
                                        <TrendIcon
                                            direction={periodComparison.averageScoreTrendDirection as TrendDirection}
                                            percentageChange={periodComparison.averageScorePercentageChange}
                                            size="medium"
                                            showTooltip={false}
                                        />
                                    )}
                                    <Typography variant="h6" component="div" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        {periodComparison?.averageScoreTrendDirection === 'improving' ? 'Improving' : 
                                         periodComparison?.averageScoreTrendDirection === 'declining' ? 'Declining' : 'Stable'}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                                    {periodComparison ? `${periodComparison.averageScorePercentageChange.toFixed(1)}% vs previous period` : 'No comparison data'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Average Score
                                </Typography>
                                <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, flex: 1 }}>
                                    {periodComparison ? Math.round(periodComparison.averageScoreCurrentPeriod) : 'N/A'}
                                </Typography>
                                <AlignmentBadgeComponent 
                                    label={periodComparison ? alignmentScoreToText(periodComparison.averageScoreCurrentPeriod) : 'No Data'}
                                    sx={{ mt: 'auto' }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Estimates
                                </Typography>
                                <Typography variant="h4" component="div" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, flex: 1 }}>
                                    {periodComparison ? periodComparison.estimationRoundsCurrentPeriod : 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                                    Current period estimates
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : null}
            
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
                <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" component="h2">
                            Alignment Trend Chart
                        </Typography>
                        <ToggleButtonGroup
                            value={dateRange}
                            exclusive
                            onChange={(_, newValue) => newValue && setDateRange(newValue)}
                            size="small"
                        >
                            <ToggleButton value="30d">30D</ToggleButton>
                            <ToggleButton value="90d">90D</ToggleButton>
                            <ToggleButton value="6m">6M</ToggleButton>
                            <ToggleButton value="1y">1Y</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
                <Box height={{ xs: "350px", sm: "450px", md: "500px" }}>
                    {renderContent()}
                </Box>
            </Paper>
        </Box>
    );
}
